/**
 * Image Utilities
 *
 * Pure utility functions for image manipulation in the verification UI.
 * These functions have no external dependencies beyond browser APIs.
 */

/**
 * Converts a base64 data URL to a Blob object.
 *
 * @param dataURL - Base64 data URL string (e.g., "data:image/jpeg;base64,...")
 * @returns Blob object with the decoded image data
 * @throws Error if the data URL format is invalid
 */
export function dataURLtoBlob(dataURL: string): Blob {
  if (!dataURL || !dataURL.includes(',')) {
    throw new Error('Invalid data URL format');
  }

  const parts = dataURL.split(',');
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch?.[1] || 'image/jpeg';
  const base64Data = parts[1];

  // Decode base64 to binary
  const binaryString = atob(base64Data);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

/**
 * Capture options for video frame capture
 */
export interface CaptureOptions {
  /** JPEG quality (0-1). Default: 0.9 */
  quality?: number;
  /** Output format. Default: 'image/jpeg' */
  format?: 'image/jpeg' | 'image/png' | 'image/webp';
}

/**
 * Captures a single frame from a video element as a data URL.
 *
 * @param video - HTMLVideoElement to capture from
 * @param options - Capture options (quality, format)
 * @returns Data URL string of the captured frame
 * @throws Error if video is not ready or capture fails
 */
export function captureVideoFrame(video: HTMLVideoElement, options: CaptureOptions = {}): string {
  const { quality = 0.9, format = 'image/jpeg' } = options;

  if (!video) {
    throw new Error('Video element is required');
  }

  if (video.readyState < 2) {
    throw new Error('Video is not ready for capture (readyState < 2)');
  }

  if (video.videoWidth === 0 || video.videoHeight === 0) {
    throw new Error('Video has no valid dimensions');
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Cannot get 2D canvas context');
  }

  ctx.drawImage(video, 0, 0);
  return canvas.toDataURL(format, quality);
}

/**
 * Captures a frame from a video element by ID.
 * Returns null on error instead of throwing (backward compatible).
 *
 * @param videoElementId - ID of the video element to capture
 * @param options - Capture options
 * @returns Data URL string or null on error
 */
export function captureVideoFrameById(
  videoElementId: string,
  options: CaptureOptions = {}
): string | null {
  const video = document.getElementById(videoElementId) as HTMLVideoElement | null;

  if (!video) {
    console.warn('[Capture] Video element not found:', videoElementId);
    return null;
  }

  try {
    return captureVideoFrame(video, options);
  } catch (error) {
    console.error('[Capture] Error capturing video frame:', error);
    return null;
  }
}

/**
 * Resizes an image data URL to fit within maximum dimensions while preserving aspect ratio.
 *
 * @param dataURL - Source image as data URL
 * @param maxWidth - Maximum width in pixels
 * @param maxHeight - Maximum height in pixels
 * @param quality - Output JPEG quality (0-1)
 * @returns Promise resolving to resized image data URL
 */
export function resizeImage(
  dataURL: string,
  maxWidth: number,
  maxHeight: number,
  quality = 0.9
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // Calculate new dimensions preserving aspect ratio
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Cannot get 2D canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = dataURL;
  });
}

/**
 * Gets image dimensions from a data URL.
 *
 * @param dataURL - Image data URL
 * @returns Promise resolving to width and height
 */
export function getImageDimensions(dataURL: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = dataURL;
  });
}
